import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import { useAuth } from '../context/AuthContext';

function ClientModal({ client, onClose, onSave, t }) {
  const [form, setForm] = useState(client || {
    name: '', vat_number: '', sector: '', contact_name: '',
    contact_email: '', contact_phone: '', address: '', notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      if (client?.id) await api.put(`/api/clients/${client.id}`, form);
      else            await api.post('/api/clients', form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{client?.id ? t('edit') : t('client_new')}</h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}
              <form id="client-form" onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('client_name')} *</label>
                  <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} required />
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('client_vat')}</label>
                    <input className="form-control" value={form.vat_number || ''} onChange={e => set('vat_number', e.target.value)} />
                  </div>
                  <div className="col">
                    <label className="form-label small fw-medium">{t('client_sector')}</label>
                    <input className="form-control" value={form.sector || ''} onChange={e => set('sector', e.target.value)} />
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('client_contact')}</label>
                    <input className="form-control" value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} />
                  </div>
                  <div className="col">
                    <label className="form-label small fw-medium">{t('client_email')}</label>
                    <input type="email" className="form-control" value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} />
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col">
                    <label className="form-label small fw-medium">{t('client_phone')}</label>
                    <input className="form-control" value={form.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} />
                  </div>
                  <div className="col">
                    <label className="form-label small fw-medium">{t('client_address')}</label>
                    <input className="form-control" value={form.address || ''} onChange={e => set('address', e.target.value)} />
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>{t('cancel')}</button>
              <button type="submit" form="client-form" className="btn btn-primary" disabled={loading}>
                {loading && <span className="spinner-border spinner-border-sm me-2" />}
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

export default function ClientsPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  function load() {
    setLoading(true);
    api.get('/api/clients').then(r => setClients(r.data)).finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleDelete(id) {
    if (!window.confirm(t('confirm_delete'))) return;
    await api.delete(`/api/clients/${id}`);
    load();
  }

  return (
    <>
      <div className="page-header">
        <h2>{t('clients_title')}</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setModal('new')}>
          <i className="bi bi-plus me-1" />
          {t('client_new')}
        </button>
      </div>

      <div className="page-body">
        <div className="card">
          {loading ? (
            <div className="card-body text-center text-muted py-4">
              <div className="spinner-border spinner-border-sm me-2" />{t('loading')}
            </div>
          ) : clients.length === 0 ? (
            <div className="card-body text-center text-muted py-4">
              <button className="btn btn-link p-0" onClick={() => setModal('new')}>{t('client_new')}</button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 48 }}></th>
                    <th>{t('client_name')}</th>
                    <th>{t('client_vat')}</th>
                    <th>{t('client_sector')}</th>
                    <th>{t('client_email')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(c => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/clients/${c.id}`)}>
                      <td onClick={e => e.stopPropagation()}>
                        {c.logo_filename
                          ? <img src={`/uploads/logos/${c.logo_filename}`} alt=""
                              style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4 }} />
                          : <div style={{ width: 32, height: 32, background: '#f1f5f9', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <i className="bi bi-building text-muted" style={{ fontSize: 14 }} />
                            </div>}
                      </td>
                      <td className="fw-medium">{c.name}</td>
                      <td className="text-muted">{c.vat_number || '—'}</td>
                      <td className="text-muted">{c.sector || '—'}</td>
                      <td>{c.contact_email || '—'}</td>
                      <td className="text-end" onClick={e => e.stopPropagation()}>
                        <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => setModal(c)}>{t('edit')}</button>
                        {user?.role === 'admin' && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c.id)}>{t('delete')}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <ClientModal
          client={modal === 'new' ? null : modal}
          t={t}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </>
  );
}
